<template>
  <div class="quiz-container">
    <div class="quiz-header">
      <h2 class="quiz-title">
        <i class="fas fa-trophy"></i>
        Country Quiz
      </h2>
    </div>

    <!-- Continent Selection -->
    <div v-if="!continentSelected" class="continent-selection">
      <div class="selection-header">
        <i class="fas fa-globe-americas"></i>
        <p class="selection-text">Choose your continent to begin the adventure:</p>
      </div>
      <div class="continent-grid">
        <button
            v-for="continent in continents"
            :key="continent"
            @click="selectContinent(continent)"
            class="continent-button"
        >
          <i :class="getContinentIcon(continent)"></i>
          <span>{{ continent }}</span>
        </button>
      </div>
    </div>

    <!-- Quiz Type Selection -->
    <div v-if="continentSelected && !quizTypeSelected" class="quiz-type-selection">
      <div class="selection-header">
        <i class="fas fa-gamepad"></i>
        <p class="selection-text">Choose your quiz type:</p>
      </div>
      <div class="quiz-type-grid">
        <button
            @click="selectQuizType('clues')"
            class="quiz-type-button"
        >
          <i class="fas fa-lightbulb"></i>
          <span class="quiz-type-title">Guess the Country</span>
          <span class="quiz-type-description">Get clues and find the country on the map</span>
        </button>
        <button
            @click="selectQuizType('flags')"
            class="quiz-type-button"
        >
          <i class="fas fa-flag"></i>
          <span class="quiz-type-title">Guess the Flag</span>
          <span class="quiz-type-description">See a flag and click the country on the map</span>
        </button>
      </div>
      <button @click="backToContinent" class="back-button">
        <i class="fas fa-arrow-left"></i>
        Back to Continents
      </button>
    </div>

    <!-- Quiz Section -->
    <div v-else-if="quizTypeSelected" class="quiz-active">
      <!-- Score Dashboard -->
      <div class="score-dashboard">
        <div class="total-score">
          <i class="fas fa-star"></i>
          <span class="score-label">Total Score</span>
          <span class="score-value">{{ totalScore }}</span>
        </div>
        <div class="medals-container">
          <div class="medal-item diamond">
            <i class="fas fa-gem"></i>
            <span class="medal-count">{{ scoreCounts.diamond }}</span>
            <span class="medal-label">Diamond</span>
          </div>
          <div class="medal-item gold">
            <i class="fas fa-medal"></i>
            <span class="medal-count">{{ scoreCounts.gold }}</span>
            <span class="medal-label">Gold</span>
          </div>
          <div class="medal-item silver">
            <i class="fas fa-award"></i>
            <span class="medal-count">{{ scoreCounts.silver }}</span>
            <span class="medal-label">Silver</span>
          </div>
          <div class="medal-item bronze">
            <i class="fas fa-certificate"></i>
            <span class="medal-count">{{ scoreCounts.bronze }}</span>
            <span class="medal-label">Bronze</span>
          </div>
        </div>
      </div>

      <!-- Countries Progress List -->
      <div v-if="completedCountries.length > 0" class="countries-progress">
        <div class="progress-header">
          <i class="fas fa-list-check"></i>
          <span class="progress-title">Countries Completed ({{ completedCountries.length }})</span>
        </div>
        <div class="countries-list">
          <div 
            v-for="country in completedCountries" 
            :key="country.name" 
            class="country-item"
            :class="country.medal"
          >
            <div class="country-flag">
              <img :src="country.flagUrl" alt="Flag" class="flag-mini">
            </div>
            <div class="country-info">
              <span class="country-name">{{ country.name }}</span>
              <span class="country-details">{{ country.cluesUsed }} clue{{ country.cluesUsed > 1 ? 's' : '' }}</span>
            </div>
            <div class="country-result">
              <i :class="getMedalIcon(country.medal)" class="medal-icon"></i>
              <span class="score-points">{{ country.score }}pts</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="quiz-loader"></div>
        <p class="loading-text">Preparing your next challenge...</p>
      </div>

      <!-- Active Quiz -->
      <div v-else>
        <!-- FLAG QUIZ MODE -->
        <div v-if="selectedQuizType === 'flags'">
          <!-- Instructions Section for Flag Quiz -->
          <div v-if="!quizCompleted && currentAttempts < 4" class="instructions-section">
            <div class="instructions-content">
              <i class="fas fa-hand-point-up fa-hand-point-right instructions-icon"></i>
              <p class="instructions-text">🚩 <strong>Click on the country</strong> that matches this flag!</p>
              <p class="instructions-subtext">You have {{ 4 - currentAttempts }} attempts remaining</p>
            </div>
          </div>

          <!-- Flag Display Section -->
          <div v-if="!quizCompleted && currentAttempts < 4" class="flag-section">
            <div class="flag-header">
              <i class="fas fa-flag"></i>
              <span class="flag-title">Identify this flag</span>
            </div>
            <div class="flag-display-container">
              <img :src="flagUrl" :alt="`Flag of ${quizData.country_name}`" class="flag-display" />
            </div>
            <div class="attempts-indicator">
              <div class="attempts-dots">
                <div
                  v-for="attempt in 4"
                  :key="attempt"
                  :class="['attempt-dot', {
                    'used': attempt <= currentAttempts,
                    'failed': attempt <= currentAttempts && !quizCompleted
                  }]"
                ></div>
              </div>
              <p class="attempts-text">Attempt {{ currentAttempts }} of 4</p>
            </div>
          </div>

          <!-- Flag Quiz Failed State -->
          <div v-if="!quizCompleted && currentAttempts >= 4" class="flag-failed-section">
            <div class="failed-header">
              <i class="fas fa-times-circle failure-icon"></i>
              <h3 class="failed-title">Out of Attempts!</h3>
              <p class="failed-subtitle">The correct answer was:</p>
            </div>

            <div class="result-card">
              <div class="flag-display">
                <img :src="flagUrl" alt="Country flag" class="result-flag">
              </div>
              <div class="result-info">
                <h4 class="country-name">{{ quizData.country_name }}</h4>
                <div class="score-earned">
                  <i class="fas fa-times-circle failure-medal"></i>
                  <span class="score-text">0 points</span>
                </div>
              </div>
            </div>

            <button @click="restartQuiz" class="continue-btn">
              <i class="fas fa-play"></i>
              Next Flag
            </button>
          </div>
        </div>

        <!-- CLUES QUIZ MODE (Original) -->
        <div v-else-if="selectedQuizType === 'clues'">
          <!-- Instructions Section -->
          <div v-if="currentClueIndex < quizData.quiz_clues?.length && !quizCompleted" class="instructions-section">
            <div class="instructions-content">
              <i class="fas fa-hand-point-up fa-hand-point-right instructions-icon"></i>
              <p class="instructions-text">📍 <strong>Click on the country</strong> based on these clues!</p>
              <p class="instructions-subtext">Find it on the map and tap to guess</p>
            </div>
          </div>

          <!-- Clue Section -->
          <div v-if="currentClueIndex < quizData.quiz_clues?.length && !quizCompleted" class="clue-section">
          <div class="clue-header">
            <i class="fas fa-lightbulb"></i>
            <span class="clue-number">Clue {{ currentClueIndex + 1 }} of {{ quizData.quiz_clues.length }}</span>
          </div>
          <div class="clue-content">
            <p class="clue-text">{{ quizData.quiz_clues[currentClueIndex] }}</p>
          </div>
          <div class="clue-actions">
            <button @click="nextClue" class="next-clue-btn">
              <i class="fas fa-arrow-right"></i>
              {{ currentClueIndex === quizData.quiz_clues.length - 1 ? 'Final Clue' : 'Next Clue' }}
            </button>
          </div>
        </div>

          <!-- Quiz Success (for clues mode) -->
          <div v-else-if="quizCompleted" class="completion-section success">
            <div class="completion-header">
              <i class="fas fa-trophy completion-icon"></i>
              <h3 class="completion-title">Excellent Work!</h3>
              <p class="completion-subtitle">You've successfully identified the country!</p>
            </div>

            <div class="result-card">
              <div class="flag-display">
                <img :src="flagUrl" alt="Country flag" class="result-flag">
              </div>
              <div class="result-info">
                <h4 class="country-name">{{ quizData.country_name }}</h4>
                <div class="score-earned">
                  <i :class="getScoreIcon()"></i>
                  <span class="score-text">{{ score }} points</span>
                  <span class="medal-earned">{{ getMedalType() }} Medal!</span>
                </div>
              </div>
            </div>

            <button @click="restartQuiz" class="continue-btn">
              <i class="fas fa-play"></i>
              Continue Adventure
            </button>
          </div>
        </div>

        <!-- Quiz Success for FLAG mode -->
        <div v-if="selectedQuizType === 'flags' && quizCompleted" class="completion-section success">
          <div class="completion-header">
            <i class="fas fa-trophy completion-icon"></i>
            <h3 class="completion-title">Excellent Work!</h3>
            <p class="completion-subtitle">You've successfully identified the flag!</p>
          </div>

          <div class="result-card">
            <div class="flag-display">
              <img :src="flagUrl" alt="Country flag" class="result-flag">
            </div>
            <div class="result-info">
              <h4 class="country-name">{{ quizData.country_name }}</h4>
              <div class="score-earned">
                <i :class="getScoreIcon()"></i>
                <span class="score-text">{{ score }} points</span>
                <span class="medal-earned">{{ getMedalType() }} Medal!</span>
              </div>
              <p class="attempts-summary">Guessed in {{ currentAttempts }} attempt{{ currentAttempts > 1 ? 's' : '' }}</p>
            </div>
          </div>

          <button @click="restartQuiz" class="continue-btn">
            <i class="fas fa-play"></i>
            Next Flag
          </button>
        </div>

      </div>
    </div>

    <!-- Results Modal -->
    <div v-if="showResultsModal" class="results-modal">
      <div class="modal-overlay"></div>
      <div class="modal-content">
        <div class="modal-header">
          <i class="fas fa-crown modal-crown"></i>
          <h3 class="modal-title">Continent Mastered!</h3>
          <p class="modal-subtitle">You've conquered all countries in {{ continentSelected }}!</p>
        </div>
        
        <div class="final-stats">
          <div class="total-score-final">
            <i class="fas fa-star"></i>
            <span class="final-score-label">Final Score</span>
            <span class="final-score-value">{{ totalScore }}</span>
          </div>
          
          <div class="medals-summary">
            <div v-for="(count, medal) in scoreCounts" :key="medal" class="medal-summary-item" :class="medal">
              <i :class="getMedalIcon(medal)"></i>
              <span class="medal-summary-count">{{ count }}</span>
              <span class="medal-summary-label">{{ medal.charAt(0).toUpperCase() + medal.slice(1) }}</span>
            </div>
          </div>
        </div>
        
        <div class="modal-actions">
          <button @click="resetQuiz" class="new-continent-btn">
            <i class="fas fa-globe"></i>
            Choose New Continent
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CountryQuiz',
  emits: ['quiz-country-name', 'update-score', 'quiz-type-changed'],
  data() {
    return {
      continents: ['Africa', 'Asia', 'Europe', 'America', 'Australia/Oceania'],
      continentSelected: null,
      quizTypeSelected: false,
      selectedQuizType: null, // 'clues' or 'flag'
      quizData: {},
      loading: false,
      currentClueIndex: 0,
      currentAttempts: 0, // For flag quiz
      shownCountries: [],
      selectedCountriesForSession: [], // Track the 5 randomly selected countries for this quiz session
      quizCompleted: false,
      score: 0,
      totalScore: 0,
      scoreCounts: {
        diamond: 0,
        gold: 0,
        silver: 0,
        bronze: 0
      },
      showResultsModal: false, // State for results modal
      completedCountries: [], // Track completed countries with results
    };
  },
  methods: {
    selectContinent(continent) {
      this.continentSelected = continent;
      this.quizTypeSelected = false; // Show quiz type selection
    },
    selectQuizType(type) {
      console.log('CountryQuiz: selectQuizType called with:', type);
      this.selectedQuizType = type;
      this.quizTypeSelected = true;
      this.$emit('quiz-type-changed', type);
      this.restartQuiz();
    },
    backToContinent() {
      this.continentSelected = null;
      this.quizTypeSelected = false;
      this.selectedQuizType = null;
    },
    async fetchQuizData() {
      this.loading = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/quiz`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            continent: this.continentSelected,
            exclude: this.shownCountries,
            quizType: this.selectedQuizType,
            selectedCountries: this.selectedCountriesForSession,
          }),
        });
        if (response.status === 400) {
          // No more countries available - quiz session complete
          this.showResultsModal = true;
          this.loading = false;

          // Submit quiz results if user is authenticated
          await this.submitQuizResults();

          return;
        }
        this.quizData = await response.json();

        // Store the selected countries for this session (returned on first call)
        if (this.quizData.selected_countries) {
          this.selectedCountriesForSession = this.quizData.selected_countries;
        }

        this.shownCountries.push(this.quizData.country_name);
        this.$emit('quiz-country-name', this.quizData.country_name);
        this.currentClueIndex = 0;
        this.currentAttempts = 0; // Reset attempts for flag quiz
        this.quizCompleted = false;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching quiz data:', error);
        this.loading = false;
      }
    },
    async restartQuiz() {
      this.showResultsModal = false; // Hide modal when restarting
      await this.fetchQuizData();
    },
    resetQuiz() {
      this.continentSelected = null;
      this.quizTypeSelected = false;
      this.selectedQuizType = null;
      this.shownCountries = [];
      this.selectedCountriesForSession = []; // Reset selected countries
      this.quizData = {};
      this.totalScore = 0;
      this.scoreCounts = {
        diamond: 0,
        gold: 0,
        silver: 0,
        bronze: 0,
      };
      this.showResultsModal = false;
      this.completedCountries = [];
      this.currentAttempts = 0;
    },
    nextClue() {
      // This method should only be called for clues quiz, not flag quiz
      if (this.selectedQuizType === 'flags') {
        // For flag quiz, this shouldn't be called - ignore it
        console.warn('nextClue() called for flag quiz - this should not happen');
        return;
      }

      if (!this.quizData.quiz_clues || this.currentClueIndex >= this.quizData.quiz_clues.length - 1) {
        // All clues exhausted without success - add to failed list and go to next country
        this.addFailedCountry();
        this.restartQuiz();
      } else {
        this.currentClueIndex += 1;
      }
    },
    completeQuiz() {
      // Prevent multiple completions for the same quiz
      if (this.quizCompleted) {
        return;
      }

      // For flag quiz, increment attempts when correct answer is clicked
      if (this.selectedQuizType === 'flags') {
        this.currentAttempts += 1;
      }

      this.quizCompleted = true;
      this.calculateScore();
    },
    handleWrongGuess() {
      // Called when user clicks wrong country in flag quiz
      console.log('handleWrongGuess called:', {
        quizType: this.selectedQuizType,
        completed: this.quizCompleted,
        currentAttempts: this.currentAttempts
      });

      if (this.selectedQuizType === 'flags' && !this.quizCompleted) {
        this.currentAttempts += 1;
        console.log('Incremented attempts to:', this.currentAttempts);

        // If max attempts reached, show the failed state
        if (this.currentAttempts >= 4) {
          console.log('Max attempts reached - showing failed state');
          this.addFailedCountry();
        }
      }
    },
    addFailedCountry() {
      // Add failed country to the list
      const cluesUsed = this.selectedQuizType === 'flags'
        ? 4
        : (this.quizData.quiz_clues?.length || 4);

      this.completedCountries.push({
        name: this.quizData.country_name,
        score: 0,
        medal: 'failed',
        cluesUsed: cluesUsed,
        flagUrl: this.flagUrl,
        failed: true
      });
    },
    calculateScore() {
      let scoreValue;
      let medalType;

      if (this.selectedQuizType === 'flags') {
        // Flag quiz scoring: based on attempts (1-4)
        if (this.currentAttempts === 1) {
          scoreValue = 20;
          medalType = 'diamond';
        } else if (this.currentAttempts === 2) {
          scoreValue = 15;
          medalType = 'gold';
        } else if (this.currentAttempts === 3) {
          scoreValue = 10;
          medalType = 'silver';
        } else {
          scoreValue = 5;
          medalType = 'bronze';
        }
      } else {
        // Clues quiz scoring: based on clues used (1-4)
        const clueCount = this.currentClueIndex + 1;
        if (clueCount === 1) {
          scoreValue = 20;
          medalType = 'diamond';
        } else if (clueCount === 2) {
          scoreValue = 15;
          medalType = 'gold';
        } else if (clueCount === 3) {
          scoreValue = 10;
          medalType = 'silver';
        } else {
          scoreValue = 5;
          medalType = 'bronze';
        }
      }

      this.score = scoreValue;
      this.scoreCounts[medalType] += 1;
      this.totalScore += this.score;
      this.$emit('update-score', this.score);
      
      // Add completed country to the list
      this.completedCountries.push({
        name: this.quizData.country_name,
        score: this.score,
        medal: this.getMedalType().toLowerCase(),
        cluesUsed: this.currentClueIndex + 1,
        flagUrl: this.flagUrl
      });
    },
    getContinentIcon(continent) {
      const icons = {
        'Africa': 'fas fa-globe-africa',
        'Asia': 'fas fa-globe-asia',
        'Europe': 'fas fa-globe-europe',
        'America': 'fas fa-globe-americas',
        'Australia/Oceania': 'fas fa-globe'
      };
      return icons[continent] || 'fas fa-globe';
    },
    getScoreIcon() {
      if (this.score === 20) return 'fas fa-gem';
      if (this.score === 15) return 'fas fa-medal';
      if (this.score === 10) return 'fas fa-award';
      return 'fas fa-certificate';
    },
    getMedalType() {
      if (this.score === 20) return 'Diamond';
      if (this.score === 15) return 'Gold';
      if (this.score === 10) return 'Silver';
      return 'Bronze';
    },
    getMedalIcon(medal) {
      const icons = {
        diamond: 'fas fa-gem',
        gold: 'fas fa-medal',
        silver: 'fas fa-award',
        bronze: 'fas fa-certificate',
        failed: 'fas fa-times-circle'
      };
      return icons[medal];
    },
    async submitQuizResults() {
      // Only submit if user is authenticated
      if (!this.isAuthenticated) {
        console.log('[CountryQuiz] User not authenticated - skipping quiz submission');
        return;
      }

      try {
        console.log('[CountryQuiz] Submitting quiz results:', {
          quiz_type: this.selectedQuizType,
          continent: this.continentSelected,
          total_score: this.totalScore,
          countries: this.completedCountries.length
        });

        // Calculate correct answers (non-failed countries)
        const correctAnswers = this.completedCountries.filter(c => !c.failed).length;

        const payload = {
          quiz_type: this.selectedQuizType, // 'clues' or 'flags'
          continent: this.continentSelected,
          countries_completed: this.completedCountries.map(c => c.name),
          total_score: this.totalScore,
          correct_answers: correctAnswers,
          total_questions: this.completedCountries.length,
          medals: this.scoreCounts,
          time_spent: 0 // Country quizzes don't track time
        };

        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/country-quiz/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.authToken}`
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('[CountryQuiz] Submission error:', data);
          return;
        }

        console.log('[CountryQuiz] Quiz results submitted successfully:', data);

        // Optionally show success message or updated stats
        // You could emit an event here or show a notification

      } catch (error) {
        console.error('[CountryQuiz] Error submitting quiz results:', error);
      }
    }
  },
  computed: {
    flagUrl() {
      return `https://flagpedia.net/data/flags/w580/${this.quizData.country_code?.toLowerCase()}.webp?v=un`;
    },
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    authToken() {
      return this.$store.state.auth.token;
    }
  },
};
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

.quiz-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header */
.quiz-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.quiz-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Continent Selection */
.continent-selection {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.selection-header {
  text-align: center;
  margin-bottom: 24px;
}

.selection-header i {
  font-size: 48px;
  color: #667eea;
  margin-bottom: 12px;
  display: block;
}

.selection-text {
  font-size: 16px;
  color: #4b5563;
  margin: 0;
  font-weight: 500;
}

.continent-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.continent-button {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  color: #374151;
}

.continent-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.continent-button i {
  font-size: 20px;
  color: inherit;
}

/* Quiz Type Selection */
.quiz-type-selection {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quiz-type-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.quiz-type-button {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.quiz-type-button:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.quiz-type-button:hover .quiz-type-title,
.quiz-type-button:hover .quiz-type-description {
  color: white;
}

.quiz-type-button i {
  font-size: 32px;
  color: #667eea;
  transition: color 0.3s ease;
}

.quiz-type-button:hover i {
  color: white;
}

.quiz-type-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.quiz-type-description {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.back-button {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #64748b;
  font-weight: 500;
  margin-top: auto;
}

.back-button:hover {
  background: #e2e8f0;
  color: #475569;
  border-color: #94a3b8;
}

.back-button i {
  font-size: 14px;
}

/* Flag Quiz Section */
.flag-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px;
}

.flag-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.flag-header i {
  color: #ef4444;
  font-size: 16px;
}

.flag-title {
  font-weight: 600;
  color: #374151;
}

.flag-display-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.flag-display {
  max-width: 100%;
  height: auto;
  max-height: 180px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.attempts-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.attempts-dots {
  display: flex;
  gap: 12px;
}

.attempt-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e2e8f0;
  transition: all 0.3s ease;
}

.attempt-dot.used {
  background: #ef4444;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.attempts-text {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
}

.flag-failed-section {
  text-align: center;
}

.failed-header {
  margin-bottom: 24px;
}

.failed-title {
  font-size: 20px;
  font-weight: 700;
  color: #ef4444;
  margin: 12px 0 8px 0;
}

.failed-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.attempts-summary {
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 0 0;
  font-style: italic;
}

/* Quiz Active Section */
.quiz-active {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Score Dashboard */
.score-dashboard {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
}

.total-score {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.total-score i {
  color: #f59e0b;
  font-size: 18px;
}

.score-label {
  font-weight: 500;
  color: #374151;
}

.score-value {
  font-weight: 700;
  font-size: 20px;
  color: #1e293b;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.medals-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.medal-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid #e2e8f0;
}

.medal-item.diamond i { color: #06b6d4; }
.medal-item.gold i { color: #f59e0b; }
.medal-item.silver i { color: #6b7280; }
.medal-item.bronze i { color: #d97706; }

.medal-count {
  font-weight: 700;
  font-size: 16px;
  color: #1e293b;
}

.medal-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
}

.quiz-loader {
  width: 40px;
  height: 40px;
  border: 4px solid #f1f5f9;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 14px;
  font-style: italic;
}

/* Instructions Section */
.instructions-section {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid #047857;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

.instructions-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.instructions-icon {
  font-size: 24px;
  color: white;
  animation: bounce 2s infinite;
}

/* Show pointing up icon on mobile, pointing right on desktop */
.instructions-icon.fa-hand-point-up {
  display: none;
}

.instructions-icon.fa-hand-point-right {
  display: inline-block;
}

@media (max-width: 768px) {
  .instructions-icon.fa-hand-point-up {
    display: inline-block;
  }
  
  .instructions-icon.fa-hand-point-right {
    display: none;
  }
}

.instructions-text {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}

.instructions-subtext {
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  margin: 0;
  font-weight: 500;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

/* Clue Section */
.clue-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px;
}

.clue-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.clue-header i {
  color: #f59e0b;
  font-size: 16px;
}

.clue-number {
  font-weight: 600;
  color: #374151;
}

.clue-content {
  margin-bottom: 20px;
}

.clue-text {
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  font-style: italic;
}

.clue-actions {
  display: flex;
  justify-content: center;
}

.next-clue-btn {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.25);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.next-clue-btn:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(72, 187, 120, 0.4);
}

.next-clue-btn:disabled {
  background: #cbd5e1;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.next-clue-btn:disabled:hover {
  background: #cbd5e1;
  transform: none;
  box-shadow: none;
}

/* Completion Section */
.completion-section {
  text-align: center;
}

.completion-header {
  margin-bottom: 24px;
}

.completion-icon {
  font-size: 48px;
  color: #f59e0b;
  margin-bottom: 12px;
  display: block;
}

.completion-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.completion-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.result-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.flag-display {
  flex-shrink: 0;
}

.result-flag {
  width: 60px;
  height: auto;
  max-height: 45px;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.result-info {
  flex: 1;
  text-align: left;
}

.country-name {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.score-earned {
  display: flex;
  align-items: center;
  gap: 8px;
}

.score-earned i {
  font-size: 16px;
}

.score-text {
  font-weight: 600;
  color: #374151;
}

.medal-earned {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.continue-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-weight: 600;
  padding: 14px 28px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin: 0 auto;
}

.continue-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Failure States */
.failure-icon {
  font-size: 48px;
  color: #ef4444;
  margin-bottom: 12px;
  display: block;
}

.failure-medal {
  color: #ef4444;
}

.result-card.failed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
}

.failure-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

.failure-btn:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

/* Results Modal */
.results-modal {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 51;
}

.modal-header {
  text-align: center;
  margin-bottom: 24px;
}

.modal-crown {
  font-size: 48px;
  color: #f59e0b;
  margin-bottom: 12px;
  display: block;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.modal-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.final-stats {
  margin-bottom: 24px;
}

.total-score-final {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
}

.total-score-final i {
  color: #f59e0b;
  font-size: 18px;
}

.final-score-label {
  font-weight: 500;
  color: #374151;
}

.final-score-value {
  font-weight: 700;
  font-size: 24px;
  color: #1e293b;
}

.medals-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.medal-summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.medal-summary-item.diamond i { color: #06b6d4; }
.medal-summary-item.gold i { color: #f59e0b; }
.medal-summary-item.silver i { color: #6b7280; }
.medal-summary-item.bronze i { color: #d97706; }

.medal-summary-count {
  font-weight: 700;
  font-size: 18px;
  color: #1e293b;
}

.medal-summary-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
}

.new-continent-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-weight: 600;
  padding: 14px 24px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin: 0 auto;
}

.new-continent-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Countries Progress */
.countries-progress {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  max-height: 200px;
  overflow-y: auto;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.progress-header i {
  color: #667eea;
  font-size: 14px;
}

.progress-title {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.countries-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.country-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(2px);
}

.country-item.diamond {
  border-left: 4px solid #06b6d4;
}

.country-item.gold {
  border-left: 4px solid #f59e0b;
}

.country-item.silver {
  border-left: 4px solid #6b7280;
}

.country-item.bronze {
  border-left: 4px solid #d97706;
}

.country-item.failed {
  border-left: 4px solid #ef4444;
  background: rgba(239, 68, 68, 0.05);
}

.country-flag {
  flex-shrink: 0;
}

.flag-mini {
  width: 24px;
  height: auto;
  max-height: 18px;
  object-fit: contain;
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.country-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.country-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.country-details {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.country-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.medal-icon {
  font-size: 14px;
}

.country-item.diamond .medal-icon { color: #06b6d4; }
.country-item.gold .medal-icon { color: #f59e0b; }
.country-item.silver .medal-icon { color: #6b7280; }
.country-item.bronze .medal-icon { color: #d97706; }
.country-item.failed .medal-icon { color: #ef4444; }

.score-points {
  font-size: 10px;
  font-weight: 600;
  color: #374151;
}

/* Custom scrollbar for countries list */
.countries-progress::-webkit-scrollbar {
  width: 4px;
}

.countries-progress::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.countries-progress::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.countries-progress::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .quiz-container {
    height: auto;
  }
  
  .medals-container {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .medal-item {
    padding: 6px;
  }
  
  .medal-label {
    font-size: 10px;
  }
  
  .result-card {
    flex-direction: column;
    text-align: center;
  }
  
  .result-info {
    text-align: center;
  }
  
  .medals-summary {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .countries-progress {
    max-height: 150px;
    padding: 12px;
  }
  
  .country-item {
    padding: 6px 8px;
    gap: 8px;
  }
  
  .flag-mini {
    width: 20px;
    max-height: 15px;
  }
  
  .country-name {
    font-size: 12px;
  }
  
  .country-details {
    font-size: 10px;
  }
}
</style>